import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { InfoIcon, RecordingsIcon, UnmutedCameraIcon } from '@streamr/tv-ui';
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styled, { css } from 'styled-components';
import { Env } from '../../Env';
import { getThumbnail } from '../../utils/thumbnail';
import { formatDate } from '../../utils/time';
import { Anchor } from '../Anchor';
import { Button } from '../Button';
import { Frame } from '../Frame';
import { showBroadcastInfo } from '../popups/BroadcastItemInfoPopup';
export function BroadcastFrame({ broadcast, onActivateBroadcastClick, onRecordingsClick, }) {
    const { name, startsAt } = broadcast;
    const formattedTime = useMemo(() => {
        return formatDate(startsAt, 'EEEE, d LLLL · h:mmaaa');
    }, [startsAt]);
    const infoButtonAnchorRef = useRef(null);
    const [isInfoPopupOpen, setIsInfoPopupOpen] = useState(false);
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const handleThumbnailChange = useCallback((newThumbnail) => {
        const objectUrl = URL.createObjectURL(newThumbnail);
        setThumbnailUrl((prev) => {
            if (prev) {
                URL.revokeObjectURL(prev);
            }
            return objectUrl;
        });
    }, []);
    useEffect(() => {
        return () => {
            if (thumbnailUrl) {
                URL.revokeObjectURL(thumbnailUrl);
            }
        };
    }, [thumbnailUrl]);
    useEffect(() => {
        if (!Env.isFeatureEnabled('ThumbnailUpload')) {
            return;
        }
        let cancelled = false;
        setThumbnailUrl(null);
        getThumbnail(broadcast.broadcasterUserId, broadcast.streamId)
            .then((file) => {
            if (cancelled)
                return;
            if (file) {
                const objectUrl = URL.createObjectURL(file);
                setThumbnailUrl((prev) => {
                    if (prev) {
                        URL.revokeObjectURL(prev);
                    }
                    return objectUrl;
                });
            }
        })
            .catch((_) => {
            // ignore the error - there might not be a thumbnail yet
        });
        return () => {
            cancelled = true;
        };
    }, [broadcast]);
    return (_jsxs(BroadcastFrameRoot, { children: [_jsxs(DetailsAndThumbnail, { children: [thumbnailUrl && (_jsx(BroadcastThumbnail, { src: thumbnailUrl, alt: "Thumbnail" })), _jsxs(Details, { children: [_jsx(BroadcastTitle, { children: name }), _jsx(BroadcastStartsAt, { children: formattedTime })] })] }), _jsxs(Buttons, { "$hasRecordings": !!onRecordingsClick, children: [_jsx(Anchor, { ref: infoButtonAnchorRef, children: _jsx(Button, { type: "button", kind: isInfoPopupOpen ? 'primary' : 'secondary', size: "mini", shape: "pill", padding: "none", icon: _jsx(InfoIcon, { fill: "currentColor" }), onClick: () => {
                                showBroadcastInfo(broadcast, {
                                    anchorRef: infoButtonAnchorRef,
                                    onOpenPopup: () => {
                                        setIsInfoPopupOpen(true);
                                    },
                                    onClosePopup: () => {
                                        setIsInfoPopupOpen(false);
                                    },
                                    onThumbnailChange: handleThumbnailChange,
                                });
                            } }) }), _jsx(Button, { type: "button", kind: "secondary", size: "mini", shape: "pill", padding: "none", icon: _jsx(UnmutedCameraIcon, {}), onClick: () => {
                            onActivateBroadcastClick?.();
                        } }), onRecordingsClick && (_jsx(Button, { type: "button", kind: "secondary", size: "mini", shape: "pill", padding: "none", icon: _jsx(RecordingsIcon, {}), onClick: () => {
                            onRecordingsClick?.();
                        } }))] })] }));
}
const BroadcastTitle = styled.h2 `
    font-size: var(--BroadcastTitle_FontSize, 2rem);
    line-height: 1;
    font-weight: 600;
    width: 100%;
    word-wrap: break-word;
`;
const BroadcastStartsAt = styled.div `
    margin-top: 0.75rem;
    font-size: var(--BroadcastStartsAt_FontSize, 1.25rem);
    line-height: 1.2em;
    width: 100%;
    opacity: 0.5;
    font-weight: 500;
`;
const BroadcastThumbnail = styled.img `
    width: 100%;
    aspect-ratio: 16/9;
    object-fit: cover;
    border-radius: 1rem;    
`;
const DetailsAndThumbnail = styled.div `
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding: 1.5rem;
    gap: 1rem;
`;
const Pad = styled.div `
    width: 100%;
    box-sizing: border-box;
    padding: 2rem;
`;
const Details = styled.div `
    flex-grow: 1;
    width: 100%;
    box-sizing: border-box;

`;
const Buttons = styled(Pad) `
    flex: 0;
    display: grid;
    gap: 1rem;
    grid-template-columns: ${({ $hasRecordings }) => ($hasRecordings ? 'auto auto auto' : 'auto auto')};
    align-self: flex-end;
    width: auto;
    padding: 1.5rem;
`;
export const BroadcastFrameListTitle = styled.div `
    font-size: 1rem;
    font-weight: 500;
    line-height: 1;
    margin: 0 auto 1rem;
    max-width: 100%;
    width: var(--BroadcastFrame_Width, 21.75rem);
`;
const BroadcastFrameRoot = styled(Frame) `
    color: #ffffff;
    margin: 0 auto;
    min-width: 0;
    min-height: var(--BroadcastFrame_MinHeight, 20rem);
    padding: 0;
    justify-content: space-between;
`;
export function BroadcastFrameList({ title, multi = false, children, }) {
    return (_jsxs(BroadcastFrameListRoot, { "$multi": multi, children: [multi && title != null && (_jsx(BroadcastFrameListTitle, { children: title })), children] }));
}
const BroadcastFrameListRoot = styled.div `
    margin: 0 auto;
    max-width: 100%;
    padding: 0 1.75rem;
    width: var(--BroadcastFrame_Width, 25.25rem);
    box-sizing: border-box;
    --tv-ui-shiny-border-radius: 2rem;

    & + &,
    ${BroadcastFrameRoot} + ${BroadcastFrameRoot} {
        margin-top: 1.5rem;
    }

    ${({ $multi }) => $multi &&
    css `
            --BroadcastTitle_FontSize: 1.5rem;
            --BroadcastStartsAt_FontSize: 1rem;
            --BroadcastFrame_Width: 44.25rem;
            --BroadcastFrame_MinHeight: none;
            --tv-ui-shiny-border-radius: 1rem;

            ${BroadcastFrameRoot} {
                gap: 2rem;                
                flex-direction: column;
                align-items: flex-start;
            }

            ${DetailsAndThumbnail} {
                flex-direction: column;
                padding: 1.5rem 1.5rem 0;
            }

            ${Buttons} {
                padding: 0 1.5rem 1.5rem;
            }

            @media (min-width: 45rem) {
                ${BroadcastFrameRoot} {
                    flex-direction: row;
                    align-items: center;
                    
                }
                ${DetailsAndThumbnail} {
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;
                    width: 100%;
                    padding: 1.5rem 0 1.5rem 1.5rem;
                    
                    ${BroadcastThumbnail} {
                        width: auto;
                        height: 4rem;
                        margin-bottom: 0;
                    }
                }
                ${Buttons} {
                    padding: 1.5rem 1.5rem 1.5rem 0;
                    align-self: center;
                }
            }
        `}
`;
